"use client"

import React, { useState, useEffect } from "react"
import Modal from "../ui/Modal"
import Input from "../ui/Input"
import Select from "../ui/Select"
import Button from "../ui/Button"
import { Sale } from "./Main"
import { api } from "../../services/Axios"
import AddProductModal from "./AddProductModal"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import {
  X,
  Trash2,
  Save,
  Plus,
} from "lucide-react"
import "../../styles/Main.css"

export interface Product {
  _id: string;
  nombre: string;
  valorVenta: number;
  cantidad: number;
}

interface EditSaleModalProps {
  sale: Sale | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedSale: Sale) => void;
  isMessageModalOpen: boolean; // Propiedad adicional para mensaje
  messageContent: string;       // Propiedad adicional para mensaje
  onCloseMessageModal: () => void; // Propiedad adicional para cerrar mensaje
}

const EditSaleModal: React.FC<EditSaleModalProps> = ({
  sale,
  isOpen,
  onClose,
  onSave,
  isMessageModalOpen,
  messageContent,
  onCloseMessageModal,
}) => {
  // Estados para sucursales y productos
  const [sucursales, setSucursales] = useState<{ _id: string; nombre: string }[]>([])
  const [selectedSucursal, setSelectedSucursal] = useState<string>("")
  const [productosVenta, setProductosVenta] = useState(sale?.productos || [])
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false)

  // Estados para totales y datos de la venta
  const [totalVenta, setTotalVenta] = useState(sale?.totalVenta || 0)
  const [totalVentaOriginal, setTotalVentaOriginal] = useState(sale?.totalVenta || 0)
  const [dineroRecibido, setDineroRecibido] = useState<string>(
    sale?.dineroRecibido ? sale.dineroRecibido.toString() : ""
  );
  const [cambio, setCambio] = useState(sale?.cambio || 0)
  const [metodoPago, setMetodoPago] = useState(sale?.metodoPago || "efectivo")
  const [descuentoPorcentaje, setDescuentoPorcentaje] = useState(sale?.descuentoPorcentaje || 0)

  // Estado para el modal de confirmación
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)

  useEffect(() => {
    if (isOpen && sale) {
      // Realizamos una copia profunda para reiniciar el estado interno
      const saleCopy = JSON.parse(JSON.stringify(sale));
      setSelectedSucursal("");
      setProductosVenta(saleCopy.productos);
      setTotalVenta(saleCopy.totalVenta);
      setTotalVentaOriginal(saleCopy.totalVenta);
      setDineroRecibido(saleCopy.dineroRecibido);
      setCambio(saleCopy.cambio);
      setMetodoPago(saleCopy.metodoPago);
      setDescuentoPorcentaje(saleCopy.descuentoPorcentaje);
      fetchSucursales();
    }
  }, [isOpen, sale]);  

  const fetchSucursales = async () => {
    try {
      const response = await api.get("/api/sucursales")
      const sucData = response.data?.sucursales || []
      setSucursales(Array.isArray(sucData) ? sucData : [])
    } catch (error) {
      console.error("Error al cargar sucursales:", error)
      setSucursales([])
    }
  }

  const handleSucursalChange = (value: string) => {
    setSelectedSucursal(value)
  }

  const handleAddProducts = (selectedProducts: Product[]) => {
    const updatedProductos = [...productosVenta]
    selectedProducts.forEach((producto) => {
      const existingProduct = updatedProductos.find(
        (p) => (p.producto as Product)._id === producto._id
      )
      if (existingProduct) {
        existingProduct.cantidad += producto.cantidad
      } else {
        updatedProductos.push({
          producto: { ...producto } as Product,
          cantidad: producto.cantidad,
          valor: producto.valorVenta,
        })
      }
    })
    setProductosVenta(updatedProductos)
    setIsAddProductModalOpen(false)
    calculateOriginalTotal(updatedProductos)
    calculateTotal(updatedProductos, descuentoPorcentaje)
  }

  const handleProductRemove = (index: number) => {
    const updatedProductos = productosVenta.filter((_, i) => i !== index)
    setProductosVenta(updatedProductos)
    calculateOriginalTotal(updatedProductos)
    calculateTotal(updatedProductos, descuentoPorcentaje)
  }

  const calculateOriginalTotal = (productos: Sale["productos"]) => {
    const subtotal = productos.reduce((acc, prod) => {
      const cant = prod.cantidad || 0
      const valor = prod.producto.valorVenta || 0
      return acc + cant * valor
    }, 0)
    setTotalVentaOriginal(Math.round(subtotal))
  }

  const calculateTotal = (productos: Sale["productos"], discount: number) => {
    const subtotal = productos.reduce(
      (acc, prod) => acc + prod.cantidad * prod.producto.valorVenta,
      0
    )
    const discountAmount = (subtotal * discount) / 100
    const total = subtotal - discountAmount
    setTotalVenta(Math.round(total))
    // Convertir dineroRecibido de string a número
    const dineroNum = parseFloat(dineroRecibido) || 0;
    setCambio(Math.round(dineroNum - total))
  }  

  const handleDineroRecibidoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setDineroRecibido(newVal);
    const valor = parseFloat(newVal) || 0;
    setCambio(Math.round(valor - totalVenta));
  };  

  const handleDescuentoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nuevoDescuento = parseFloat(e.target.value) || 0
    setDescuentoPorcentaje(nuevoDescuento)
    calculateTotal(productosVenta, nuevoDescuento)
  }

  const handleSave = () => {
    setIsConfirmationModalOpen(true)
  }

  const confirmSave = () => {
    if (sale) {
      onSave({
        ...sale,
        productos: productosVenta,
        totalVenta,
        dineroRecibido: parseFloat(dineroRecibido) || 0,
        cambio,
        metodoPago,
        descuentoPorcentaje,
      })
      setIsConfirmationModalOpen(false)
    }
  }

  if (!isOpen || !sale) return null

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Editar Venta" size="lg">
        {/* Contenedor scrollable para limitar la altura */}
        <div className="max-h-[65vh] overflow-y-auto pr-2">
          <div className="space-y-6">
            {/* Grupo: Sucursal y Botón Agregar Productos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Sucursal"
                options={[{ value: "", label: "Seleccione una sucursal" }, ...sucursales.map((s) => ({ value: s._id, label: s.nombre }))]}
                value={selectedSucursal}
                onChange={handleSucursalChange}
                fullWidth
              />
              <Button 
                onClick={() => setIsAddProductModalOpen(true)} 
                disabled={!selectedSucursal}
                fullWidth
              >
                <Plus className="w-4 h-4 mr-1" /> Agregar Productos
              </Button>
            </div>

            {/* Grupo: Totales y Datos de la Venta */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input label="Venta Total Original" type="number" value={totalVentaOriginal} readOnly fullWidth />
              <Input label="Venta Total (con descuentos)" type="number" value={totalVenta} readOnly fullWidth />
              <Input label="Cambio" type="number" value={cambio} readOnly fullWidth />
              <Input
                label="Dinero Recibido"
                type="number"
                value={dineroRecibido}
                onChange={handleDineroRecibidoChange}
                fullWidth
              />
              <Input
                label="Descuento (%)"
                type="number"
                value={descuentoPorcentaje}
                onChange={handleDescuentoChange}
                fullWidth
              />
              <Select
                label="Método de Pago"
                options={[
                  { value: "efectivo", label: "Efectivo" },
                  { value: "tarjeta", label: "Tarjeta" },
                  { value: "transferencia", label: "Transferencia" },
                  { value: "nequi", label: "Nequi" },
                ]}
                value={metodoPago}
                onChange={(val) => setMetodoPago(val)}
                fullWidth
              />
            </div>

            {/* Lista de Productos Seleccionados */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Productos en la Venta</h3>
              <div className="max-h-56 overflow-y-auto space-y-3">
                {productosVenta.map((prod, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <span className="text-gray-700 font-medium">
                      {prod.producto.nombre} - x{prod.cantidad}
                    </span>
                    <Button variant="danger" onClick={() => handleProductRemove(index)}>
                      <Trash2 className="w-4 h-4 mr-1" />Eliminar
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer fijo */}
        <div className="flex justify-end space-x-3 mt-6">
          <Button onClick={onClose} variant="outline">
            <X className="w-4 h-4 mr-1" />Cancelar
          </Button>
          <Button onClick={handleSave} disabled={cambio <= 0}>
            <Save className="w-4 h-4 mr-1" />Guardar
          </Button>
        </div>
      </Modal>

      {/* Modal para agregar productos */}
      <AddProductModal
        sucursalId={selectedSucursal || ""}
        isOpen={isAddProductModalOpen}
        onClose={handleAddProducts}
      />

      {/* Confirmation Modal para confirmar guardado */}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        title="Confirmar Guardado"
        message="¿Estás seguro de que deseas guardar los cambios de esta venta?"
        onConfirm={confirmSave}
        onCancel={() => setIsConfirmationModalOpen(false)}
      />
    </>
  )
}

export default EditSaleModal;
